import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useMeta from "@/hooks/useMeta";
import { Card, Button, Input, Select, Spinner, EmptyState, Avatar, TimelineBadge, StatusBadge, Modal, Label, UserSelect, Tooltip, usePagination, Pagination } from "@/components/UI";
import TaskModal from "@/components/TaskModal";
import { STATUS_META, TASK_TYPE_META } from "@/lib/constants";
import { ClipboardList, Plus, Search, ArrowRightCircle, Trash2 } from "lucide-react";

export default function Backlog() {
  const { isAdmin, user } = useAuth();
  const meta = useMeta();
  const [tasks, setTasks] = useState(null);
  const [filter, setFilter] = useState({ search: "", type: "", projectId: "", status: "", timeline: "" });
  const [selected, setSelected] = useState([]);
  const [modal, setModal] = useState({ open: false, mode: "view", task: null });
  const [moveModal, setMoveModal] = useState({ open: false, task: null });
  const [targetAssignee, setTargetAssignee] = useState("");
  const [targetSprint, setTargetSprint] = useState("");
  const [moving, setMoving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [bulkSprintModal, setBulkSprintModal] = useState(false);
  const [bulkSprintId, setBulkSprintId] = useState("");
  const [bulkMoving, setBulkMoving] = useState(false);

  const activeSprint = useMemo(() => meta?.sprints?.find((s) => s.status === "active"), [meta]);
  const nextSprint = useMemo(
    () =>
      [...(meta?.sprints || [])]
        .filter((s) => s.status === "planning")
        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0],
    [meta]
  );

  const load = () =>
    api
      .get("/tasks")
      .then((r) => setTasks(r.data))
      .catch(() => toast.error("Gagal memuat backlog"));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!tasks) return [];
    return tasks.filter((t) => {
      if (t.status === "planned") return false;
      const matchesSearch = !filter.search || t.title.toLowerCase().includes(filter.search.toLowerCase());
      const matchesType = !filter.type || (t.type || (t.project_id ? "project" : "internal")) === filter.type;
      const matchesProject = !filter.projectId || String(t.project_id) === filter.projectId;
      const matchesStatus = !filter.status || t.status === filter.status;
      const matchesTimeline = !filter.timeline || t.timeline_status === filter.timeline;
      return matchesSearch && matchesType && matchesProject && matchesStatus && matchesTimeline;
    });
  }, [tasks, filter]);

  const pg = usePagination(filtered, 15);

  // Hanya task berstatus backlog yang bisa dipilih untuk bulk action
  const selectableTasks = filtered.filter((t) => t.status === "backlog");
  const allSelected = selectableTasks.length > 0 && selectableTasks.every((t) => selected.includes(t.id));
  const toggleAll = () => setSelected(allSelected ? [] : selectableTasks.map((t) => t.id));
  const toggleOne = (task) => {
    if (task.status !== "backlog") return;
    setSelected((p) => (p.includes(task.id) ? p.filter((x) => x !== task.id) : [...p, task.id]));
  };

  const openCreate = () => setModal({ open: true, mode: "create", task: null });
  const openView = (task) => setModal({ open: true, mode: "view", task });

  const startMove = (e, task) => {
    e.stopPropagation();
    if (!activeSprint) {
      toast.error("Tidak ada sprint aktif. Aktifkan sprint dulu.");
      return;
    }
    if (isAdmin) {
      setTargetAssignee(task.assignee_id || "");
      setTargetSprint(String(activeSprint.id));
      setMoveModal({ open: true, task });
    } else {
      if (task.assignee_id && task.assignee_id !== user.id) {
        toast.error("Task sudah dialokasikan ke PIC lain");
        return;
      }
      requestKanban(task);
    }
  };

  const moveTask = async (task, assigneeId, sprintId) => {
    setMoving(true);
    try {
      await api.put(`/tasks/${task.id}`, {
        assignee_id: assigneeId || null,
        status: "to_do",
        sprint_id: sprintId,
      });
      toast.success("Task dipindahkan ke Kanban (Sprint)");
      setMoveModal({ open: false, task: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal memindahkan ke Kanban");
    } finally {
      setMoving(false);
    }
  };

  const requestKanban = async (task) => {
    if (!activeSprint) {
      toast.error("Tidak ada sprint aktif. Aktifkan sprint dulu.");
      return;
    }
    setMoving(true);
    try {
      await api.patch(`/tasks/${task.id}/status`, { status: "waiting_approval" });
      toast.success("Permintaan dipindahkan ke Kanban dikirim. Menunggu approval Admin/PM.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal mengajukan task");
    } finally {
      setMoving(false);
    }
  };

  const removeSelected = async () => {
    if (selected.length === 0) return;
    setDeleting(true);
    try {
      await Promise.all(selected.map((id) => api.delete(`/tasks/${id}`)));
      toast.success(`${selected.length} task dihapus`);
      setSelected([]);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menghapus task");
    } finally {
      setDeleting(false);
    }
  };

  const confirmMove = () => {
    if (!targetAssignee) {
      toast.error("Pilih PIC terlebih dahulu");
      return;
    }
    if (!targetSprint) {
      toast.error("Pilih sprint terlebih dahulu");
      return;
    }
    moveTask(moveModal.task, targetAssignee, targetSprint);
  };

  const confirmBulkMove = async () => {
    if (!bulkSprintId) {
      toast.error("Pilih sprint terlebih dahulu");
      return;
    }
    setBulkMoving(true);
    try {
      await api.post("/tasks/bulk-assign-sprint", { task_ids: selected, sprint_id: bulkSprintId });
      toast.success(`${selected.length} task dipindahkan ke Kanban (Sprint)`);
      setSelected([]);
      setBulkSprintModal(false);
      setBulkSprintId("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal memindahkan task ke Kanban");
    } finally {
      setBulkMoving(false);
    }
  };

  if (!tasks) return <Spinner />;

  return (
    <div className="mx-auto max-w-6xl space-y-5" data-testid="backlog-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Backlog</h1>
          <p className="text-sm text-slate-400">
            Semua task beserta statusnya — backlog, sprint, hingga yang sudah cleared.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button onClick={openCreate} data-testid="create-backlog-button">
              <Plus size={16} /> Tambah Task
            </Button>
          )}
        </div>
      </div>

      <Card className="p-4">
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1.3fr_1fr_1fr_auto]">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              value={filter.search}
              onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
              placeholder="Cari task..."
              className="pl-9"
              data-testid="backlog-search"
            />
          </div>
          <Select
            value={filter.type}
            onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))}
            data-testid="backlog-type-filter"
          >
            <option value="">Jenis</option>
            <option value="project">Project</option>
            <option value="internal">Internal</option>
            <option value="konsep">Konsep</option>
          </Select>
          <Select
            value={filter.projectId}
            onChange={(e) => setFilter((f) => ({ ...f, projectId: e.target.value }))}
            data-testid="backlog-project-filter"
          >
            <option value="">Project</option>
            {meta?.projects?.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
          <Select
            value={filter.status}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
            data-testid="backlog-status-filter"
          >
            <option value="">Status</option>
            {Object.entries(STATUS_META)
              .filter(([value]) => !["waiting_approval", "replaced"].includes(value))
              .map(([value, m]) => (
                <option key={value} value={value}>{m.label}</option>
              ))}
          </Select>
          <Select
            value={filter.timeline}
            onChange={(e) => setFilter((f) => ({ ...f, timeline: e.target.value }))}
            data-testid="backlog-timeline-filter"
          >
            <option value="">Timeline</option>
            <option value="on_track">On Track</option>
            <option value="late">Terlambat</option>
          </Select>
          <Button variant="ghost" onClick={() => setFilter({ search: "", type: "", projectId: "", status: "", timeline: "" })}>
            Reset
          </Button>
        </div>
      </Card>

      {isAdmin && selected.length > 0 && (
        <div className="flex items-center justify-end gap-2" data-testid="backlog-bulk-bar">
          <span className="text-xs text-slate-400">{selected.length} task terpilih</span>
          <Button
            size="sm"
            onClick={() => {
              setBulkSprintId(activeSprint ? String(activeSprint.id) : "");
              setBulkSprintModal(true);
            }}
            data-testid="backlog-bulk-add-kanban"
          >
            <ArrowRightCircle size={14} /> Add to Sprint  
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => setDeleteTarget(true)}
            data-testid="backlog-bulk-delete"
          >
            <Trash2 size={14} /> Hapus
          </Button>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Tidak ada task" subtitle="Belum ada task yang cocok dengan filter." />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full table-fixed text-sm">
            <thead className="border-b border-slate-800 bg-slate-950/60 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                {isAdmin && (
                  <th className="w-10 px-4 py-3">
                    <input type="checkbox" className="sf-check" checked={allSelected} onChange={toggleAll} data-testid="backlog-select-all" />
                  </th>
                )}
                <th className="px-4 py-3">Task</th>
                <th className="px-4 py-3" style={{ width: "18%" }}>Project / Tahapan</th>
                <th className="px-4 py-3" style={{ width: "11%" }}>Jenis</th>
                {isAdmin && <th className="px-4 py-3 text-center" style={{ width: "8%" }}>Bobot</th>}
                <th className="px-4 py-3" style={{ width: "13%" }}>Deadline</th>
                <th className="px-4 py-3" style={{ width: "12%" }}>Status</th>
                {isAdmin ? (
                  <th className="px-3 py-3 text-center" style={{ width: "8%" }}>PIC</th>
                ) : (
                  <>
                    <th className="px-3 py-3 text-center" style={{ width: "10%" }}>PIC</th>
                    <th className="px-4 py-3">Aksi</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {pg.paged.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => openView(t)}
                  className="cursor-pointer border-b border-slate-800/60 last:border-0 hover:bg-white/[0.02]"
                  data-testid={`backlog-row-${t.id}`}
                >
                  {isAdmin && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="sf-check disabled:cursor-not-allowed disabled:opacity-30" checked={selected.includes(t.id)} disabled={t.status !== "backlog"} onChange={() => toggleOne(t)} data-testid={`backlog-checkbox-${t.id}`} />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <p className="truncate font-medium text-slate-200">{t.title}</p>
                    {t.description && <p className="truncate text-xs text-slate-500">{t.description}</p>}
                  </td>
                  <td className="truncate px-4 py-3 text-xs text-slate-400">
                    {t.project?.name ? (
                      <span className="block truncate text-slate-300">{t.project.name}</span>
                    ) : (
                      <span className="block text-slate-600">—</span>
                    )}
                    {t.phase?.name && <span className="block truncate text-slate-500">{t.phase.name}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <TypeBadge type={t.type || (t.project_id ? "project" : "internal")} />
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-center">
                      {t.weight != null ? (
                        <>
                          <span className="font-bold text-[#E0B70B]">{t.weight}</span>
                          <span className="ml-0.5 text-[10px] text-slate-500">pts</span>
                        </>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <TimelineBadge date={t.deadline} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="px-3 py-3 text-center">
                    <Tooltip label={t.assignee?.name || "Unassigned"}>
                      <Avatar src={t.assignee?.avatar_url} name={t.assignee?.name || "?"} size={34} />
                    </Tooltip>
                  </td>
                  {!isAdmin && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {t.status !== "backlog" ? (
                        <span className="text-xs text-slate-600">Sudah masuk sprint</span>
                      ) : !t.assignee_id || t.assignee_id === user.id ? (
                        <Button size="sm" onClick={(e) => startMove(e, t)} data-testid={`backlog-add-kanban-${t.id}`}>
                          <ArrowRightCircle size={14} /> Minta Approval
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-600">Ditugaskan ke lain</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={pg.page} totalPages={pg.totalPages} total={pg.total} perPage={pg.perPage} onChange={pg.setPage} />
        </Card>
      )}

      <TaskModal
        open={modal.open}
        mode={modal.mode}
        task={modal.task}
        meta={meta}
        onClose={() => setModal({ open: false, mode: "view", task: null })}
        onSaved={load}
      />

      <Modal
        open={moveModal.open}
        onClose={() => setMoveModal({ open: false, task: null })}
        title="Add to Sprint"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setMoveModal({ open: false, task: null })} data-testid="move-modal-cancel">
              Batal
            </Button>
            <Button onClick={confirmMove} disabled={!targetAssignee || !targetSprint || moving} data-testid="move-modal-confirm">
              {moving ? "Memproses..." : "Add to Sprint"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-300">
            Pindahkan task <strong>{moveModal.task?.title}</strong> ke Sprint. Pilih PIC dan sprint tujuan.
          </p>
          <div>
            <Label>PIC (Assignee)</Label>
            <UserSelect
              users={meta?.users || []}
              value={targetAssignee}
              onChange={setTargetAssignee}
              placeholder="— Pilih PIC —"
              data-testid="move-modal-assignee"
            />
          </div>
          <div>
            <Label>Sprint</Label>
            <Select value={targetSprint} onChange={(e) => setTargetSprint(e.target.value)} data-testid="move-modal-sprint">
              {activeSprint && <option value={activeSprint.id}>Minggu Ini · {activeSprint.name}</option>}
              {nextSprint && <option value={nextSprint.id}>Minggu Depan · {nextSprint.name}</option>}
            </Select>
          </div>
        </div>
      </Modal>

      <Modal
        open={bulkSprintModal}
        onClose={() => setBulkSprintModal(false)}
        title="Add to Sprint (Bulk)"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setBulkSprintModal(false)} data-testid="bulk-move-modal-cancel">
              Batal
            </Button>
            <Button onClick={confirmBulkMove} disabled={!bulkSprintId || bulkMoving} data-testid="bulk-move-modal-confirm">
              {bulkMoving ? "Memproses..." : "Add to Sprint"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-300">
            Pindahkan <strong>{selected.length} task</strong> terpilih ke Sprint. Pilih sprint tujuan.
          </p>
          <div>
            <Label>Sprint</Label>
            <Select value={bulkSprintId} onChange={(e) => setBulkSprintId(e.target.value)} data-testid="bulk-move-modal-sprint">
              {activeSprint && <option value={activeSprint.id}>Minggu Ini · {activeSprint.name}</option>}
              {nextSprint && <option value={nextSprint.id}>Minggu Depan · {nextSprint.name}</option>}
            </Select>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Hapus Task"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Batal</Button>
            <Button variant="danger" onClick={removeSelected} disabled={deleting} data-testid="confirm-delete-task">
              {deleting ? "Menghapus..." : `Hapus ${selected.length} Task`}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          Yakin ingin menghapus <strong>{selected.length} task</strong> yang dipilih? Tindakan ini tidak bisa dibatalkan.
        </p>
      </Modal>
    </div>
  );
}

function TypeBadge({ type }) {
  const meta = TASK_TYPE_META[type] || TASK_TYPE_META.project;
  return (
    <span
      className="rounded-md px-2 py-0.5 text-[11px] font-semibold"
      style={{ color: meta.color, backgroundColor: `${meta.color}15` }}
    >
      {meta.label}
    </span>
  );
}
